import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  EnvelopeIcon, 
  BriefcaseIcon,
  TrashIcon,
  ShieldCheckIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import useNotification from '../../hooks/useNotification';
import axiosInstance from '../../api/Axios';
import { API_ENDPOINTS } from '../../constants/api';
import ConfirmActionModal from '../modals/ConfirmActionModal';

const UserManagement = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [activatingUserId, setActivatingUserId] = useState(null);
  const [deactivatingUserId, setDeactivatingUserId] = useState(null);
  
  // Состояние для модальных окон
  const [modalState, setModalState] = useState({
    isOpen: false,
    action: null,
    userId: null,
    userName: null
  });

  // Проверяем, является ли текущий пользователь администратором
  const isAdmin = user?.roles?.includes('ADMIN');

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axiosInstance.get(API_ENDPOINTS.GET_DEVELOPERS);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.descr || 'Ошибка при загрузке пользователей');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const openModal = (action, userId, userName) => {
    setModalState({
      isOpen: true,
      action,
      userId,
      userName
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      action: null,
      userId: null,
      userName: null
    });
  };

  const handleDeleteUser = async (userId, userName) => {
    setDeletingUserId(userId);
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.DELETE_USER}/${userId}`);
      showSuccess(`Пользователь "${userName}" успешно удален`);
      // Обновляем список пользователей
      setUsers(users.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      showError(error.response?.data?.descr || 'Ошибка при удалении пользователя');
    } finally {
      setDeletingUserId(null);
      closeModal();
    }
  };

  const handleActivateDeveloper = async (userId, userName) => {
    setActivatingUserId(userId);
    try {
      await axiosInstance.put(`${API_ENDPOINTS.ACTIVATE_DEVELOPER}/${userId}/activate`);
      showSuccess(`Разработчик "${userName}" успешно активирован`);
      // Обновляем статус пользователя
      setUsers(users.map(u => 
        u.id === userId ? { ...u, isActive: true } : u
      ));
    } catch (error) {
      console.error('Error activating developer:', error);
      showError(error.response?.data?.descr || 'Ошибка при активации разработчика');
    } finally {
      setActivatingUserId(null);
      closeModal();
    }
  };

  const handleDeactivateDeveloper = async (userId, userName) => {
    setDeactivatingUserId(userId);
    try {
      await axiosInstance.put(`${API_ENDPOINTS.DEACTIVATE_DEVELOPER}/${userId}/deactivate`);
      showSuccess(`Разработчик "${userName}" успешно деактивирован`);
      // Обновляем статус пользователя
      setUsers(users.map(u => 
        u.id === userId ? { ...u, isActive: false } : u
      ));
    } catch (error) {
      console.error('Error deactivating developer:', error);
      showError(error.response?.data?.descr || 'Ошибка при деактивации разработчика');
    } finally {
      setDeactivatingUserId(null);
      closeModal();
    }
  };

  const handleModalConfirm = () => {
    const { action, userId, userName } = modalState;
    
    switch (action) {
      case 'activate':
        handleActivateDeveloper(userId, userName);
        break;
      case 'deactivate':
        handleDeactivateDeveloper(userId, userName);
        break;
      case 'delete':
        handleDeleteUser(userId, userName);
        break;
      default:
        closeModal();
    }
  };

  const getRoleDisplay = (roles) => {
    if (!roles || !Array.isArray(roles)) return 'Пользователь';
    
    if (roles.includes('ADMIN')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          Администратор
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <UserIcon className="w-3 h-3 mr-1" />
        Пользователь
      </span>
    );
  };

  const getStatusDisplay = (isActive) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckIcon className="w-3 h-3 mr-1" />
          Активен
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XMarkIcon className="w-3 h-3 mr-1" />
          Деактивирован
        </span>
      );
    }
  };

  const getActionButtons = (userItem) => {
    const isCurrentUser = userItem.id === user?.id;
    const isActive = userItem.isActive !== false;
    const isLoading = 
      deletingUserId === userItem.id || 
      activatingUserId === userItem.id || 
      deactivatingUserId === userItem.id;

    return (
      <div className="flex space-x-2">
        {isActive ? (
          <button
            onClick={() => openModal('deactivate', userItem.id, userItem.name)}
            disabled={isLoading || isCurrentUser}
            className="inline-flex items-center px-3 py-1.5 border border-orange-300 shadow-sm text-xs font-medium rounded text-orange-700 bg-white hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <XMarkIcon className="w-3 h-3 mr-1" />
            Деактивировать
          </button>
        ) : (
          <>
            <button
              onClick={() => openModal('activate', userItem.id, userItem.name)}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 border border-green-300 shadow-sm text-xs font-medium rounded text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckIcon className="w-3 h-3 mr-1" />
              Активировать
            </button>
            <button
              onClick={() => openModal('delete', userItem.id, userItem.name)}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 border border-red-300 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrashIcon className="w-3 h-3 mr-1" />
              Удалить
            </button>
          </>
        )}
      </div>
    );
  };

  console.log('UserManagement render:', { 
    user, 
    isAdmin, 
    userRoles: user?.roles,
    users, 
    isLoading, 
    error 
  });

  // Если пользователь еще загружается
  if (!user) {
    console.log('User not loaded yet');
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка данных пользователя...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Доступ запрещен. Требуются права администратора.</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка пользователей...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          <div className="font-semibold mb-2">Ошибка загрузки:</div>
          <div>{error}</div>
          <button 
            onClick={fetchUsers}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  const activeUsers = users.filter(userItem => userItem.isActive !== false);
  const inactiveUsers = users.filter(userItem => userItem.isActive === false);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Управление пользователями</h2>
          <div className="text-sm text-gray-500">
            Всего пользователей: {users.length}
          </div>
        </div>

        {/* Активные пользователи */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <CheckIcon className="w-5 h-5 mr-2 text-green-500" />
            Активные пользователи ({activeUsers.length})
          </h3>
          {activeUsers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Нет активных пользователей</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-medium text-gray-700">Пользователь</th>
                    <th className="text-left p-3 font-medium text-gray-700">Email</th>
                    <th className="text-left p-3 font-medium text-gray-700">Должность</th>
                    <th className="text-left p-3 font-medium text-gray-700">Роль</th>
                    <th className="text-left p-3 font-medium text-gray-700">Статус</th>
                    <th className="text-left p-3 font-medium text-gray-700">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {activeUsers.map((userItem) => (
                    <tr key={userItem.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center">
                          <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{userItem.name}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center">
                          <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700">{userItem.email}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center">
                          <BriefcaseIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700">{userItem.role}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {getRoleDisplay(userItem.roles)}
                      </td>
                      <td className="p-3">
                        {getStatusDisplay(userItem.isActive)}
                      </td>
                      <td className="p-3">
                        {getActionButtons(userItem)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Деактивированные пользователи */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <XMarkIcon className="w-5 h-5 mr-2 text-gray-500" />
            Деактивированные пользователи ({inactiveUsers.length})
          </h3>
          {inactiveUsers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Нет деактивированных пользователей</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left p-3 font-medium text-gray-700">Пользователь</th>
                    <th className="text-left p-3 font-medium text-gray-700">Email</th>
                    <th className="text-left p-3 font-medium text-gray-700">Должность</th>
                    <th className="text-left p-3 font-medium text-gray-700">Роль</th>
                    <th className="text-left p-3 font-medium text-gray-700">Статус</th>
                    <th className="text-left p-3 font-medium text-gray-700">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {inactiveUsers.map((userItem) => (
                    <tr key={userItem.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center">
                          <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="font-medium text-gray-900">{userItem.name}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center">
                          <EnvelopeIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700">{userItem.email}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center">
                          <BriefcaseIcon className="w-4 h-4 text-gray-400 mr-2" />
                          <span className="text-gray-700">{userItem.role}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {getRoleDisplay(userItem.roles)}
                      </td>
                      <td className="p-3">
                        {getStatusDisplay(userItem.isActive)}
                      </td>
                      <td className="p-3">
                        {getActionButtons(userItem)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно подтверждения */}
      <ConfirmActionModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={handleModalConfirm}
        action={modalState.action}
        userName={modalState.userName}
        isLoading={
          deletingUserId === modalState.userId || 
          activatingUserId === modalState.userId || 
          deactivatingUserId === modalState.userId
        }
      />
    </div>
  );
};

export default UserManagement; 